import {Box, Group, TextInput} from '@mantine/core';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {getAccessibleOrInaccessibleStorableId, GridType} from 'constants/SampleConstants';
import * as Context from 'Context';
import {callSampleStatusWarningModal} from 'inventory/sample-status/SampleStatusWarningModal';
import {FillDirection} from 'lookup-config/LookupConfigModalConstants';
import {getFillDirectionIconName, getFillDirectionText, getGridTypeName} from 'samples/grids/GridUtil';
import {ScanToMoveCellByCoordinates, ScanToMoveGridCell} from 'samples/grids/ScanToMoveGridUtil';
import {
  formatGridCoordinates,
  InvalidPositionError,
  InvalidPositionFormatError,
  NoPositionError,
  parseGridPosition,
  PositionOutOfBoundsError,
} from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Spinner from 'ui-library/Spinner';
import * as Modals from 'util/Modals';
import {Optional} from 'util/Types';

export interface ScanToMoveGridHeaderProps {
  /** API id of the grid. */
  gridId: string;
  /** Type of the grid. Controls how grid position is displayed in the table. */
  gridType: GridType;
  /** User-chosen direction to fill the grid cells, across rows or columns */
  fillDirection: FillDirection;
  /** Mapping from containers to coordinates. This includes both the existing containers (marked with
   * `isPreexisting` set to true) and new ones being scanned in. Coordinates of the form A1, B2, etc. */
  containersByCoordinates: ScanToMoveCellByCoordinates;
  /** Coordinates that have focus for scanning. */
  scanCoordinates: string;
  /** Select different coordinates to scan into. */
  onChangeScanCoordinates: (coordinates: string) => void;
  /** Dimensions of the grid. */
  gridDimensions: {width: number; height: number};
  /** Action to take when a barcode is entered */
  onBarcodeSubmit: (scannedBarcode: string, onFinish: () => void) => void;
  /** Whether a scan is currently being processed */
  isProcessingScan: boolean;
  /** Optional action to take when the scanning is finalized */
  onSubmit?: () => void;
  /** Optional action to take when cancelling out of scanning mode */
  onCancel?: () => void;
}

export default function ScanToMoveGridHeader(props: ScanToMoveGridHeaderProps): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [barcodeText, setBarcodeText] = useState('');
  // Tracking raw text field input from the user
  // Set to null when position is selected from grid or table
  const [userEnteredPosition, setUserEnteredPosition] = useState<Optional<string>>(null);
  // Tracking text field input from the user in the alpha-numeric format
  // Set to null when position is selected from grid or table
  const [userEnteredFormattedPosition, setUserEnteredFormattedPosition] = useState<Optional<string>>(null);
  // Ref to the barcode input field to focus on it after we lose focus
  const barcodeInput = useRef<HTMLInputElement>(null);

  function focusBarcodeInput(): void {
    setTimeout(() => {
      barcodeInput.current?.focus();
    }, 0);
  }

  const getTitle = (): string => {
    if (props.isProcessingScan) {
      return 'Processing input';
    }
    if (isSubmitting) {
      return 'Submitting...';
    }
    return 'Accepting scanner input';
  };

  const getHeaderIcon = (): JSX.Element => {
    if (props.isProcessingScan || isSubmitting) {
      return <Spinner />;
    }
    return <BenchlingIcon className="u-mini-spacing-right" icon="scan" />;
  };

  const isGridFull = useMemo(
    () =>
      Object.values(props.containersByCoordinates).filter((cell) => cell.container != null).length ===
      props.gridDimensions.height * props.gridDimensions.width,
    [props.containersByCoordinates, props.gridDimensions.height, props.gridDimensions.width]
  );

  const isCellModified = (cell: ScanToMoveGridCell): boolean =>
    cell.container != null && (!cell.isPreexisting || cell.originalGridCoordinates != null);

  const isGridModified = useMemo(
    () =>
      Object.values(props.containersByCoordinates).some(
        // Grid is modified if there are cells with containers that are not preexisting or have been moved
        (cell) => isCellModified(cell)
      ),
    [props.containersByCoordinates]
  );

  // The text field display will show the user-typed value, which could be a numeric or alpha-numeric
  // coordinate, or the alpha-numeric coordinate selected from clicking the grid or table which would reset
  // the userEnteredPosition to null.
  const inputPositionText = userEnteredPosition ?? props.scanCoordinates;

  const inputPositionError = useMemo(() => {
    let error = null;
    let rowIndex = null;
    let columnIndex = null;

    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position: inputPositionText,
        gridHeight: props.gridDimensions.height,
        gridWidth: props.gridDimensions.width,
      }));
    } catch (e) {
      if (e instanceof PositionOutOfBoundsError) {
        error = 'Position does not exist';
      } else if (e instanceof InvalidPositionFormatError) {
        error = 'Position must be in format: A1, A01, or 1';
      } else if (e instanceof NoPositionError) {
        error = 'Position is required';
      } else {
        throw e;
      }
    }

    if (
      rowIndex != null &&
      columnIndex != null &&
      props.containersByCoordinates[formatGridCoordinates(rowIndex, columnIndex)]?.container != null
    ) {
      error = 'Position is already filled';
    }
    return error;
  }, [
    inputPositionText,
    props.containersByCoordinates,
    props.gridDimensions.height,
    props.gridDimensions.width,
  ]);

  const disableSubmitBarcode =
    inputPositionError != null || isSubmitting || props.isProcessingScan || isGridFull;

  const handleInputPositionOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const position = e.target.value;
    let rowIndex = null;
    let columnIndex = null;

    // The user-entered position could be numeric, but the scanCoordinate needs to be in alpha-numeric format
    try {
      ({rowIndex, columnIndex} = parseGridPosition({
        position,
        gridHeight: props.gridDimensions.height,
        gridWidth: props.gridDimensions.width,
      }));
    } catch (e) {
      // Error reporting done separately
      if (!(e instanceof InvalidPositionError)) {
        throw e;
      }
    }

    // Track the raw input from the user
    setUserEnteredPosition(position);
    // Use alpha-numeric format if the position is valid or an empty string otherwise
    const coordinates =
      rowIndex != null && columnIndex != null ? formatGridCoordinates(rowIndex, columnIndex) : '';
    // Track the formatted input from the user to avoid having to reparse it later
    setUserEnteredFormattedPosition(coordinates);
    // Set the scanCoordinate to update the highlighting in the grid and table
    props.onChangeScanCoordinates(coordinates);
  };

  useEffect(() => {
    // If the position is selected from the grid or table, override the previously-entered information in the
    // input position text field.
    if (userEnteredFormattedPosition != null && props.scanCoordinates !== userEnteredFormattedPosition) {
      // Coordinates are from clicking on the grid or table, reset user input to null and focus on barcode input
      setUserEnteredPosition(null);
      setUserEnteredFormattedPosition(null);
      focusBarcodeInput();
    }
  }, [props.scanCoordinates, userEnteredFormattedPosition]);

  const handleOnSubmit = async (): Promise<void> => {
    const analyticsEventGroupId = Analytics.makeAnalyticsEventId();
    const analyticsActionOrigin =
      props.gridType === GridType.BOX ? ActionOrigin.INVENTORY_BOX_PAGE : ActionOrigin.INVENTORY_PLATE_PAGE;
    const sampleIdsToCoordinates = new Map(
      Object.values(props.containersByCoordinates)
        .filter((cell) => isCellModified(cell))
        .map((cell) => [
          getAccessibleOrInaccessibleStorableId(cell.container!),
          {columnIndex: cell.columnIndex, rowIndex: cell.rowIndex},
        ])
    );
    const {shouldProceed, selectedSampleIds} = await callSampleStatusWarningModal({
      sampleIds: [...sampleIdsToCoordinates.keys()],
      trackingParams: {
        actionOrigin: analyticsActionOrigin,
        actionAttempted: 'move_location',
        eventGroupId: analyticsEventGroupId,
      },
    });

    if (shouldProceed) {
      if (selectedSampleIds.length > 0) {
        setIsSubmitting(true);
        Context.usingAsync(
          {
            analyticsMetadata: {
              actionOrigin: analyticsActionOrigin,
              actionMethod: ActionMethod.SCAN_TO_MOVE,
              eventGroupId: analyticsEventGroupId,
            },
          },
          null,
          () => {
            StorableActions.bulkMoveStorablesWithDataMods(
              selectedSampleIds.map((sampleId) => ({
                id: sampleId,
                parentStorableData: {
                  id: props.gridId,
                  gridCellColumnIndex: sampleIdsToCoordinates.get(sampleId)!.columnIndex,
                  gridCellRowIndex: sampleIdsToCoordinates.get(sampleId)!.rowIndex,
                },
              })),
              props.onSubmit,
              () => {
                setIsSubmitting(false);
              }
            );
          }
        );
      } else {
        // TODO(BNCH-91368): evaluate the appropriate response to a no-op submit
        props.onSubmit?.();
      }
    }
  };

  const handleOnCancel = (): void => {
    Modals.confirm(
      'Cancel scan mode',
      'All un-submitted scan records will be removed. This action cannot be undone.',
      ButtonStyle.NEGATIVE,
      {
        acceptText: 'Continue',
        rejectText: 'Back',
        enableRedesign: true,
        medium: true,
        onSubmit: props.onCancel,
      }
    );
  };

  const handleBarcodeSubmit = (): void => {
    if (barcodeText !== '') {
      props.onBarcodeSubmit(barcodeText, () => {
        setBarcodeText('');
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleBarcodeSubmit();
    }
  };

  const handleBarcodeOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setBarcodeText(e.target.value);
  };

  useEffect(() => {
    // only focus on barcode if the user has not entered a position
    if (userEnteredPosition == null) {
      focusBarcodeInput();
    }
  }, [props.containersByCoordinates, props.isProcessingScan, props.scanCoordinates, userEnteredPosition]);

  return (
    <div className="gridHeader" {...getTestProps({component: 'ScanToMoveGridHeader', element: 'root'})}>
      <div className="u-lr-split u-mini-spacing-left">
        <div
          className="gridHeader-title"
          {...getTestProps({component: 'ScanToMoveGridHeader', element: 'title'})}
        >
          {getHeaderIcon()}
          {getTitle()}
        </div>
        <div className="buttonToolbar">
          <div className="buttonGroup">
            <Button
              text="Cancel"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              onClick={handleOnCancel}
              disabled={isSubmitting}
            />
          </div>
          <div className="buttonGroup">
            <Tooltip
              text="No changes detected. Please scan containers."
              isDisabled={isGridModified || isGridFull}
            >
              <Button
                text="Submit"
                buttonStyle={ButtonStyle.PRIMARY}
                size={ButtonSize.SMALL}
                onClick={handleOnSubmit}
                disabled={!isGridModified || isSubmitting}
                dataTestAttrs={{component: 'ScanToMoveGridHeader', element: 'submitButton'}}
              />
            </Tooltip>
          </div>
        </div>
      </div>
      <div className="u-vertical-stretch u-mini-spacing-left">
        <div className="u-spacing-right u-spacing-top">
          <TextInput
            size="md"
            className="u-inline-form-input-sm"
            label="Input position"
            value={inputPositionText}
            onChange={handleInputPositionOnChange}
            error={!isGridFull && inputPositionError != null ? inputPositionError : null}
            disabled={isGridFull || isSubmitting}
          />
        </div>
        <div className="u-vertical-stretch u-height-full">
          <div className="u-lr-split u-spacing-top u-spacing-right">
            <TextInput
              size="md"
              className="u-inline-form-input-lg u-mini-spacing-right"
              label="Input barcode"
              autoFocus={true}
              placeholder={disableSubmitBarcode ? '' : 'Fill in barcode'}
              value={barcodeText}
              onChange={handleBarcodeOnChange}
              onKeyDown={handleKeyDown}
              disabled={disableSubmitBarcode}
              ref={barcodeInput}
              {...getTestProps({component: 'ScanToMoveGridHeader', element: 'barcodeInput'})}
            />
            <div className="u-spacing-top">
              <Tooltip
                text="Please scan or enter a barcode"
                isDisabled={barcodeText !== '' || disableSubmitBarcode}
              >
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  flexWidth={true}
                  size={ButtonSize.SMALL}
                  text="Fill position"
                  onClick={handleBarcodeSubmit}
                  disabled={barcodeText === '' || disableSubmitBarcode}
                />
              </Tooltip>
            </div>
          </div>
          <div className="u-spacing-top">
            <div>
              <label className="fieldLabel-label">Fill direction</label>
            </div>
            <Group fz="lg" color={GRAY_DARKER} mt="xs">
              <BenchlingIcon icon={getFillDirectionIconName(props.fillDirection)} />
              <Box ml="xs">{getFillDirectionText(props.fillDirection)}</Box>
            </Group>
          </div>
        </div>
      </div>
      {isGridFull && (
        <div className="u-spacing-top">
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text={`This ${getGridTypeName(props.gridId)} is full.`}
          />
        </div>
      )}
    </div>
  );
}
